import { Button, Form, Modal, Select, Space } from "antd";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { fetchAccessibleOrganizations, fetchFinancialYears } from "../../api/tenantApi";
import { useTenantStore } from "../../stores/tenantStore";

export function TenantYearSwitcher({
  open,
  mandatory,
  onClose
}: {
  open: boolean;
  mandatory?: boolean;
  onClose: () => void;
}) {
  const [form] = Form.useForm();
  const setTenant = useTenantStore((x) => x.setTenant);
  const tenant = useTenantStore();
  const queryClient = useQueryClient();
  const selectedOrgId = Form.useWatch("orgId", form);
  const orgQuery = useQuery({
    queryKey: ["tenant-organizations"],
    queryFn: fetchAccessibleOrganizations,
    enabled: open
  });
  const fyQuery = useQuery({
    queryKey: ["tenant-financial-years", selectedOrgId],
    queryFn: () => fetchFinancialYears(selectedOrgId),
    enabled: open && !!selectedOrgId
  });

  const orgOptions = useMemo(
    () => (orgQuery.data ?? []).map((x) => ({ value: String(x.id), label: x.name || x.code || String(x.id) })),
    [orgQuery.data]
  );
  const fyOptions = useMemo(
    () => (fyQuery.data ?? []).map((x) => ({ value: String(x.id), label: x.code || String(x.id), code: x.code || String(x.id) })),
    [fyQuery.data]
  );

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      orgId: tenant.orgId ?? undefined,
      financialYearId: tenant.financialYearId ?? undefined
    });
  }, [form, open, tenant.financialYearId, tenant.orgId]);

  useEffect(() => {
    if (!open || !orgOptions.length) return;
    const current = form.getFieldValue("orgId");
    const existing = orgOptions.some((x) => x.value === current);
    if (existing) return;
    const preferred = tenant.orgId && orgOptions.some((x) => x.value === tenant.orgId)
      ? tenant.orgId
      : orgOptions[0].value;
    form.setFieldValue("orgId", preferred);
  }, [form, open, orgOptions, tenant.orgId]);

  useEffect(() => {
    if (!open || !selectedOrgId || !fyOptions.length) return;
    const current = form.getFieldValue("financialYearId");
    const existing = fyOptions.some((x) => x.value === current);
    if (existing) return;
    const preferred = tenant.financialYearId && fyOptions.some((x) => x.value === tenant.financialYearId)
      ? tenant.financialYearId
      : fyOptions[0].value;
    form.setFieldValue("financialYearId", preferred);
  }, [form, fyOptions, open, selectedOrgId, tenant.financialYearId]);

  return (
    <Modal
      title="Company / Financial Year"
      open={open}
      closable={!mandatory}
      maskClosable={!mandatory}
      onCancel={onClose}
      footer={null}
      width={400}
      className="compact-switch-modal"
    >
      <Form form={form} layout="vertical" onFinish={(values) => {
        const org = orgOptions.find((x) => x.value === values.orgId);
        const fy = fyOptions.find((x) => x.value === values.financialYearId);
        setTenant(values.orgId, org?.label ?? "Company", fy?.code ?? fy?.label ?? "", values.financialYearId);
        queryClient.clear();
        onClose();
      }}>
        <Form.Item name="orgId" label="Company" rules={[{ required: true }]}>
          <Select loading={orgQuery.isLoading} options={orgOptions} placeholder="Select company" onChange={() => form.setFieldValue("financialYearId", undefined)} />
        </Form.Item>
        <Form.Item name="financialYearId" label="Financial Year" rules={[{ required: true }]}>
          <Select loading={fyQuery.isLoading} options={fyOptions} placeholder="Select financial year" />
        </Form.Item>
        <Space>
          <Button type="primary" htmlType="submit">Use Selection</Button>
          {!mandatory ? <Button onClick={onClose}>Cancel</Button> : null}
        </Space>
      </Form>
    </Modal>
  );
}
